export const safeJsonStringify = (text: string | object) => {
  return JSON.stringify(text).replace(/</g, '\\u003c');
};

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/**
 * HTML 엔티티를 원문자로 되돌린다.
 *
 * WordPress 는 본문에 `&` 를 **십진 엔티티 `&#038;`** 로 저장한다(`&amp;` 가 아니다).
 * 그래서 이름 있는 엔티티만 치환하면 CMS 에서 온 텍스트가 화면에 `&#038;` 그대로
 * 노출된다(예: 시리즈 라벨 "Sample Collection &#038; Registration Guide").
 *
 * 이름·십진(`&#038;`)·십육진(`&#x26;`)을 **한 번의 스캔**으로 처리한다 — 여러 번
 * 치환하면 `&amp;#038;` 같은 입력이 두 번 풀려 원문이 훼손된다.
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';

  return text.replace(
    /&(#[Xx][0-9A-Fa-f]+|#\d+|[A-Za-z]+);/g,
    (match, body) => {
      if (body[0] !== '#') {
        return NAMED_HTML_ENTITIES[body.toLowerCase()] ?? match;
      }

      const isHex = body[1] === 'x' || body[1] === 'X';
      const codePoint = parseInt(
        isHex ? body.slice(2) : body.slice(1),
        isHex ? 16 : 10,
      );

      // 코드포인트 범위를 벗어나면 fromCodePoint 가 던지므로 원문을 그대로 둔다
      if (
        !Number.isFinite(codePoint) ||
        codePoint < 1 ||
        codePoint > 0x10ffff
      ) {
        return match;
      }

      return String.fromCodePoint(codePoint);
    },
  );
};

/**
 * CMS 설정 포스트 본문(HTML)을 `key: value` 파싱이 가능한 라인 배열로 정규화한다.
 *
 * 에디터픽·시리즈 등 "본문에 설정을 적는" 소스 포스트들이 공유한다. 이 두 파서는
 * **같은 관례**를 쓴다 — 값이 빈 `image:` 라인이 "바로 다음 본문 이미지"를 가리킨다.
 * 그래서 img 를 지우지 않고 `__IMG__{src}` 마커 라인으로 남겨 등장 위치를 보존한다.
 * 나머지 태그는 라인 구분(<br>·</p>·</figure>)으로 바꾸거나 지운다.
 *
 * 엔티티 디코딩은 **여기서 한 번만** 한다 — WP 는 본문을 렌더할 때 wptexturize 로
 * `'`→`&#8216;/&#8217;`, `&`→`&#038;` 로 바꿔 내려주므로, 풀지 않으면 화면에
 * `Testing for &#8216;innate&#8217;` 가 그대로 노출된다. 호출하는 파서가 값마다 다시
 * 풀면 안 된다 — `&amp;#038;` 같은 입력이 두 번 풀려 원문이 훼손된다.
 *
 * 마커 접두사나 정규식을 바꾸면 이 함수를 쓰는 파서 전부가 함께 영향을 받는다.
 */
export const toCmsSourceLines = (content: string): string[] =>
  content
    .replace(/<img[^>]*\ssrc="([^"]*)"[^>]*>/gi, '\n__IMG__$1\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|figure)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split('\n')
    // 태그를 지운 뒤에 푼다 — 순서를 뒤집으면 `&lt;p&gt;` 가 진짜 태그로 되살아나 지워진다
    .map(line => decodeHtmlEntities(line).trim())
    .filter(Boolean);

/**
 * `toCmsSourceLines` 가 남긴 이미지 마커 라인을 읽는다 — 캡처 그룹 1이 src 다.
 *
 * 마커를 만드는 쪽과 읽는 쪽이 같은 접두사를 써야 하므로 여기 함께 둔다.
 * 파서에서 각자 리터럴로 적으면 접두사를 바꿀 때 한쪽만 고치고 조용히 어긋난다.
 */
export const CMS_IMAGE_MARKER_LINE = /^__IMG__(.*)$/;

/**
 * CMS 본문(HTML)을 화면·구조화 데이터에 쓸 순수 텍스트로 바꾼다.
 *
 * 태그를 지운 **뒤에** 엔티티를 푼다 — 순서를 뒤집으면 `&lt;script&gt;` 가 진짜 태그로
 * 되살아나 다음 스캔에서 지워진다. 십진 엔티티까지 `decodeHtmlEntities` 로 한 번에
 * 처리해야 한다: WP 는 렌더 시 `'`→`&#8217;`, `&`→`&#038;` 로 텍스처화하므로 이름
 * 엔티티만 풀면 `institution&#8217;s` 가 그대로 노출된다.
 */
export const stripHtmlTags = (html: string): string => {
  if (!html) return '';

  return decodeHtmlEntities(html.replace(/<[^>]*>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();
};

export const getLastPath = (path: string) => {
  return path.split('/').pop() ?? '';
};

export const sanitizeText = (text: string) => {
  return text.replaceAll(' ', '_').replaceAll("'", '');
};
